/**
 * Component icons are bundled with the app under `src/assets/component-icons/`
 * and keyed by the orchestrator component's `name` (e.g. `nmap`, `dns_records`,
 * `_input`). The orchestrator returns only the bare name in `component.icon`,
 * which is not a renderable URL — this resolver turns a name into the bundled
 * asset URL so the UI can `<img src>` it.
 *
 * To add or replace an icon: drop `<component_name>.webp` into
 * `src/assets/component-icons/`. No code change needed — the folder is globbed
 * at build time.
 */

// webpack (CRA5) build-time glob of every webp in the icon folder.
const ctx = (require as unknown as { context: (dir: string, deep: boolean, re: RegExp) => any })
  .context('../assets/component-icons', false, /\.webp$/)

const ICONS: Record<string, string> = {}
for (const key of ctx.keys()) {
  // key looks like './nmap.webp' → 'nmap'
  const name = key.replace(/^\.\//, '').replace(/\.webp$/, '')
  const mod = ctx(key)
  ICONS[name] = mod?.default ?? mod
}

/**
 * Resolve a component to its bundled icon URL.
 * @param name  the component's `name` (primary key, matches the file basename)
 * @param slug  optional secondary key (the legacy `icon` slug) to fall back on
 * @returns the asset URL, or `null` when no icon is bundled (caller shows a
 *          placeholder — every render site already falls back to a <Box> icon).
 */
export function resolveComponentIcon(
  name?: string | null,
  slug?: string | null,
): string | null {
  if (name && ICONS[name]) return ICONS[name]
  if (slug && ICONS[slug]) return ICONS[slug]
  return null
}

/** All bundled component-icon names (for tooling / tests). */
export const bundledComponentIconNames = (): string[] => Object.keys(ICONS)
